/**
 * Vinci Live Pulse · client tracker hook with advanced intelligence.
 *
 * On every route change fires an enriched beacon carrying:
 *   • session_id            — sticky per tab
 *   • fingerprint           — persistent Vinci Digital ID (localStorage)
 *   • path_trail            — last 12 URLs the visitor walked
 *   • time_on_prev_ms       — time spent on the previous page
 *   • screen / languages    — device intel (no PII)
 *   • tz_offset_min         — for silent timezone cross-check
 * The server enriches IP-side with ISP / ASN / connection type / zip.
 */
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const SS_KEY = "vinci_pulse_session";
const SS_PRECISE_KEY = "vinci_pulse_precise_sent";
const LS_FP_KEY = "vinci_digital_id";
const LS_TRAIL_KEY = "vinci_pulse_trail";
const BASE = process.env.REACT_APP_BACKEND_URL;


function _sessionId() {
  try {
    let s = sessionStorage.getItem(SS_KEY);
    if (!s) {
      s = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SS_KEY, s);
    }
    return s;
  } catch { return "s_anon"; }
}

async function _sha256Hex(text) {
  try {
    const enc = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    // Fallback: cheap FNV-1a-ish hash for very old browsers.
    let h = 2166136261 >>> 0;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h.toString(16).padStart(8, "0").repeat(8);
  }
}

function _canvasFP() {
  try {
    const c = document.createElement("canvas");
    c.width = 240; c.height = 60;
    const ctx = c.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#F60";
    ctx.fillRect(20, 12, 100, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Vinci · 🛡️ · Pulse", 4, 6);
    ctx.fillStyle = "rgba(102,204,0,0.7)";
    ctx.fillText("Vinci · 🛡️ · Pulse", 4, 24);
    return c.toDataURL();
  } catch { return "canvas-blocked"; }
}

async function _computeFingerprint() {
  try {
    const cached = localStorage.getItem(LS_FP_KEY);
    if (cached && cached.length === 64) return cached;
    const bits = [
      navigator.userAgent,
      navigator.platform,
      navigator.language, (navigator.languages || []).join(","),
      screen.width + "x" + screen.height + "@" + (window.devicePixelRatio || 1),
      new Date().getTimezoneOffset(),
      Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "",
      navigator.hardwareConcurrency || 0,
      navigator.deviceMemory || 0,
      navigator.maxTouchPoints || 0,
      _canvasFP(),
    ].join("||");
    const fp = await _sha256Hex(bits);
    localStorage.setItem(LS_FP_KEY, fp);
    return fp;
  } catch { return ""; }
}

function _pushTrail(path) {
  try {
    const raw = sessionStorage.getItem(LS_TRAIL_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (arr[arr.length - 1] !== path) arr.push(path);
    while (arr.length > 12) arr.shift();
    sessionStorage.setItem(LS_TRAIL_KEY, JSON.stringify(arr));
    return arr;
  } catch { return [path]; }
}

async function _sendBeacon(payload) {
  try {
    const url = `${BASE}/api/pulse/track`;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url,
        new Blob([body], { type: "application/json" }));
      if (ok) return;
    }
    await axios.post(url, payload, { timeout: 3000 });
  } catch { /* silent */ }
}

export function usePulseTracker({ brand = "", category = "" } = {}) {
  const loc = useLocation();
  const lastSent = useRef("");
  const enteredAt = useRef(Date.now());

  const sendPrecise = useCallback((sessionId) => {
    try {
      if (sessionStorage.getItem(SS_PRECISE_KEY) === "1") return;
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await axios.post(`${BASE}/api/pulse/precise-track`, {
              session_id: sessionId,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
              altitude_m: pos.coords.altitude || null,
            }, { timeout: 3000 });
            sessionStorage.setItem(SS_PRECISE_KEY, "1");
          } catch { /* silent */ }
        },
        () => { /* denied */ },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
      );
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const key = `${loc.pathname}${loc.search}::${brand}::${category}`;
    if (lastSent.current === key) return;
    const timeOnPrev = Date.now() - enteredAt.current;
    lastSent.current = key;
    enteredAt.current = Date.now();

    const send = async (reason = "route") => {
      const sid = _sessionId();
      const fp = await _computeFingerprint();
      const trail = _pushTrail(loc.pathname);
      _sendBeacon({
        session_id: sid,
        fingerprint: fp,
        page_url: window.location.href,
        referrer: document.referrer || "",
        brand: brand || "",
        category: category || "",
        path_trail: trail,
        time_on_prev_ms: reason === "route" ? Math.min(timeOnPrev, 5 * 60 * 1000) : 0,
        screen: `${screen.width}x${screen.height}@${window.devicePixelRatio || 1}`,
        languages: (navigator.languages || [navigator.language]).slice(0, 4).join(","),
        tz_offset_min: -new Date().getTimezoneOffset(),
      });
      if (reason === "route") sendPrecise(sid);
    };
    send("route");

    // Heartbeat every 90 s so the visitor stays in the 30-min live-now
    // window as long as their tab is open (route-change-only beacons
    // dropped idle sessions from the map).
    const hb = setInterval(() => {
      if (typeof document !== "undefined"
          && document.visibilityState !== "visible") return;
      send("heartbeat");
    }, 90000);
    return () => clearInterval(hb);
  }, [loc.pathname, loc.search, brand, category, sendPrecise]);
}

export function trackBrandView({ brand, category }) {
  _sendBeacon({
    session_id: _sessionId(),
    page_url: window.location.href,
    referrer: document.referrer || "",
    brand: brand || "",
    category: category || "",
  });
}
